import React, { useCallback, useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import PostThreadView from "./PostThreadView";
import { useAuth } from "@/scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  parsePostPermalinkSearch,
  permalinkForSocialPost,
} from "@/lib/permalinks";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { useSocialPaths } from "@/lib/socialPaths";

/**
 * Canonical social post/thread page: /p/:postId
 * Also nested under /orgs/:orgId/social/p/:postId (workplace Circa Social).
 * Optional ?org= (or actor context) enables workplace privileges (official delete).
 */
const SocialPostPermalinkPage = () => {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const actor = useOrgSocialActor();
  const paths = useSocialPaths();

  const [post, setPost] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const { returnPath } = parsePostPermalinkSearch(location.search);
  const orgParam = searchParams.get("org");
  const resolvedOrgId =
    actor?.orgId ||
    (orgParam &&
    !orgParam.includes("/") &&
    !orgParam.includes("://") &&
    orgParam.trim()
      ? orgParam.trim()
      : null);
  const isOrgCommunity = Boolean(resolvedOrgId);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/user/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        });
        if (!cancelled) {
          setCurrentUserId(data?.user_id || data?.userId || null);
        }
      } catch {
        /* non-fatal for read-only viewing */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const normalizePost = useCallback((p) => {
    if (!p) return p;
    const id = p.social_post_id ?? p.post_id ?? p.postId;
    const isRepost =
      p.repost_kind === "bare" || p.repost_kind === "quote";
    return {
      ...p,
      post_id: id,
      social_post_id: id,
      title: isRepost
        ? p.repost_kind === "quote"
          ? null
          : p.title ?? null
        : p.title ?? null,
      created_at: p.created_at || p.createdAt,
      deleted_at: p.deleted_at || p.deletedAt || null,
      is_official: Boolean(p.is_official),
      author_org: p.author_org || null,
      user: p.is_official ? null : p.user || null,
      user_id: p.is_official ? null : p.user_id ?? null,
      can_delete: Boolean(p.can_delete),
      like_count: p.like_count ?? p.likeCount ?? 0,
      reply_count: p.reply_count ?? p.replyCount ?? 0,
      repost_count: p.repost_count ?? p.repostCount ?? 0,
      repost_kind: p.repost_kind ?? null,
      viewer_has_reposted: Boolean(p.viewer_has_reposted),
    };
  }, []);

  const loadPost = useCallback(async () => {
    if (!accessToken || !postId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/social/posts/${postId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        }
      );
      let next = normalizePost(data);
      // Social GET always serializes as visitor (can_delete false for official).
      // When opened from Community Space (?org=), restore publish delete rights.
      if (
        resolvedOrgId &&
        next?.is_official &&
        !next.can_delete
      ) {
        try {
          const presenceRes = await axios.get(
            `${API_BASE_URL}/org/${encodeURIComponent(resolvedOrgId)}/social/presence`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              withCredentials: true,
            }
          );
          if (presenceRes.data?.can_social_publish) {
            next = { ...next, can_delete: true };
          }
        } catch {
          /* non-fatal — delete stays hidden */
        }
      }
      setPost(next);
    } catch (err) {
      console.error("Failed to load social permalink post:", err);
      setPost(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [accessToken, postId, normalizePost, resolvedOrgId]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const community = post?.community || null;
  const communityId =
    community?.community_id ?? post?.community_id ?? post?.communityId ?? null;
  const communityName = community?.display_name || null;
  const authorOrg = post?.author_org || post?.authorOrg || null;
  const isOfficial = Boolean(post?.is_official || post?.isOfficial);

  const contextLine =
    isOfficial && authorOrg?.name
      ? authorOrg.name
      : communityName
        ? communityName
        : "Circa Social";

  const canonicalPath = permalinkForSocialPost({ postId });

  const backLabel = "Back";

  const handleCopyLink = async () => {
    if (!canonicalPath) return;
    const url = `${window.location.origin}${canonicalPath}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath);
      return;
    }
    if (communityId) {
      navigate(paths.community(communityId));
      return;
    }
    navigate(paths.home());
  };

  if (!accessToken) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6 text-sm text-muted-foreground text-center">
        Loading thread…
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="p-4 md:p-6 space-y-4 max-w-lg mx-auto">
        <h1 className="text-xl font-semibold">Post not found</h1>
        <p className="text-sm text-muted-foreground">
          This post may not exist, or you do not have access to it.
        </p>
        <Button type="button" variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 flex flex-col items-center pb-8">
      <div className="w-full max-w-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-3">
          <div className="min-w-0 space-y-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 -ml-2"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {backLabel}
            </Button>
            <p className="text-xs text-muted-foreground pl-1">{contextLine}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-muted-foreground"
            onClick={handleCopyLink}
          >
            {copied ? (
              <Check className="h-4 w-4 mr-1.5" />
            ) : (
              <Copy className="h-4 w-4 mr-1.5" />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <PostThreadView
          variant="page"
          hidePageHeader
          post={post}
          isOpen
          onClose={handleBack}
          accessToken={accessToken}
          currentUserId={currentUserId}
          orgId={isOrgCommunity ? resolvedOrgId : undefined}
          orgName={authorOrg?.name || communityName || undefined}
          surface={isOrgCommunity ? "org-community" : "community"}
          onPostUpdate={(updated) => {
            if (updated?.purged) {
              handleBack();
              return;
            }
            setPost(normalizePost(updated));
          }}
        />
      </div>
    </div>
  );
};

export default SocialPostPermalinkPage;
